export const SITE_CONFIG = {
  name: "tutorials.co.uk",
  title: "Free AI Training for UK Businesses & Charities",
  description:
    "Free, practical AI training built for UK businesses and charities, not generic global courses. Tutorials, courses and learning paths that get your team using AI the same week. No card needed.",
  // Canonical web origin. Live serving domain is the www host; the bare
  // tutorials.co.uk should 301-redirect here (Vercel/DNS). This single value
  // feeds canonical tags, OG URLs, metadataBase, sitemap, robots and JSON-LD.
  url: "https://www.tutorials.co.uk",
  locale: "en_GB",
  language: "en-GB",
  creator: "Coaley Peak Ltd",
  parentCompany: {
    name: "Coaley Peak",
    registeredName: "Coaley Peak Ltd",
    url: "https://coaleypeak.co.uk?utm_source=tutorials.co.uk&utm_medium=referral&utm_campaign=website",
    trademark: "Coaley Peak®",
    // Statutory company details (UK Companies Act requires these on business
    // sites); also lend the site real-world credibility. Source: coaleypeak.co.uk.
    companyNumber: "11783676",
    incorporatedIn: "England & Wales",
    vat: "GB374552088",
    ico: "ZA505303",
    certifications: ["ISO 9001", "ISO 27001"],
    phone: "0800 494 7725",
    email: "support@coaleypeak.co.uk",
    address: {
      line1: "The Limes, Bayshill Road",
      city: "Cheltenham",
      postcode: "GL50 3AW",
      country: "United Kingdom",
    },
    // Approximate coordinates of the Cheltenham base, for LocalBusiness geo.
    geo: { latitude: 51.9, longitude: -2.0667 },
  },
  social: {
    linkedin: "",
    youtube: "",
  },
} as const;

/**
 * The site's named author. Tutorials are written by Stephen Grindley, Company
 * Director of Coaley Peak (the parent company). His profile page carries the
 * Person schema and the bylines link to it, giving the content real author
 * authority (E-E-A-T) rather than an anonymous brand name.
 */
export const AUTHOR = {
  name: "Stephen Grindley",
  slug: "stephen-grindley",
  url: "/about/stephen-grindley",
  jobTitle: "Company Director",
  role: "Company Director, Coaley Peak",
  short:
    "AI automation and data strategy specialist, and Company Director of Coaley Peak.",
  linkedin: "https://uk.linkedin.com/in/stephengrindley",
  profile: "https://www.coaleypeak.co.uk/team/stephen-grindley",
} as const;

export const NAVIGATION = {
  main: [
    {
      label: "Learn",
      href: "/tutorials",
      children: [
        {
          label: "AI Tutorials",
          href: "/tutorials",
          description: "Step-by-step guides, free to read",
        },
        {
          label: "Learning Paths",
          href: "/paths",
          description: "Ordered routes from idea to skill",
        },
        {
          label: "AI Glossary",
          href: "/glossary",
          description: "Key terms, properly explained",
        },
        {
          label: "Common Questions",
          href: "/questions",
          description: "Straight answers to AI questions",
        },
      ],
    },
    { label: "Train Your Team", href: "/train-your-team" },
    { label: "Local Training", href: "/local-training" },
    { label: "Courses", href: "/courses" },
  ],
  footer: {
    learn: [
      { label: "AI Tutorials", href: "/tutorials" },
      { label: "AI Learning Paths", href: "/paths" },
      { label: "AI Courses", href: "/courses" },
      { label: "AI Glossary", href: "/glossary" },
      { label: "AI Questions", href: "/questions" },
      { label: "AI Blog", href: "/blog" },
    ],
    resources: [
      { label: "Local AI Training", href: "/local-training" },
      { label: "Free AI Resources", href: "/free" },
      { label: "AI Career Guide", href: "/career" },
      { label: "AI Tools", href: "/tools" },
      { label: "AI Training Funding", href: "/funding" },
      { label: "AI Events", href: "/events" },
    ],
    cities: [
      { label: "Birmingham", href: "/west-midlands/birmingham" },
      { label: "Bristol", href: "/bristol/bristol" },
      { label: "Cheltenham", href: "/gloucestershire/cheltenham" },
      { label: "Gloucester", href: "/gloucestershire/gloucester" },
      { label: "London", href: "/london/london" },
      { label: "Leicester", href: "/leicestershire/leicester" },
    ],
    company: [
      { label: "About", href: "/about" },
      { label: "Contact", href: "/contact" },
      { label: "Newsletter", href: "/newsletter" },
      { label: "Train Your Team", href: "/train-your-team" },
    ],
    legal: [
      { label: "Privacy Policy", href: "/privacy" },
      { label: "Terms & Conditions", href: "/terms" },
    ],
  },
  mobile: [
    { label: "Home", href: "/", icon: "Home" },
    { label: "Tutorials", href: "/tutorials", icon: "BookOpen" },
    { label: "My Learning", href: "/dashboard", icon: "GraduationCap" },
    { label: "Menu", href: "#menu", icon: "Menu" },
  ],
} as const;

export const CATEGORIES = [
  {
    slug: "ai-fundamentals",
    title: "AI Fundamentals",
    description: "What AI and machine learning actually are, and how the whole thing works under the bonnet.",
    icon: "Brain",
  },
  {
    slug: "prompt-engineering",
    title: "Prompt Engineering",
    description: "How to ask AI assistants for what you want, and get it back right, every time.",
    icon: "MessageSquare",
  },
  {
    slug: "ai-tools",
    title: "AI Tools & Platforms",
    description: "Step-by-step guides to the AI tools and platforms worth your team's time.",
    icon: "Wrench",
  },
  {
    slug: "ai-for-business",
    title: "AI for Business",
    description: "Where AI earns its keep in a small business: marketing, customer service, the daily admin.",
    icon: "Briefcase",
  },
  {
    slug: "ai-coding",
    title: "AI Coding & Development",
    description: "Build AI into real software, with Python, APIs and the tools developers reach for.",
    icon: "Code",
  },
] as const;

export const DIFFICULTY_LEVELS = {
  beginner: { label: "Beginner", colour: "emerald", icon: "Sprout" },
  intermediate: { label: "Intermediate", colour: "amber", icon: "Flame" },
  advanced: { label: "Advanced", colour: "rose", icon: "Rocket" },
} as const;

// In-person training is charged by the day, for a group.
export const TRAINING_DAY_RATE = 1049; // £ per training day
export const TRAINING_DAY_RATE_LABEL = "1,049 + VAT"; // display form; keep in sync with TRAINING_DAY_RATE
export const TRAINING_MIN_ATTENDEES = 8; // minimum group size

/**
 * Paid one-to-one and small-team AI tutoring, delivered in person or remotely.
 * A separate, chargeable service from the free online material.
 */
export const TUTORING = {
  heading: "One-to-one and team AI tutoring",
  blurb:
    "Prefer something more personal? We also offer paid one-to-one and small-team AI tutoring, in person or over a video call, shaped around exactly what you and your team want to learn.",
  shortNote:
    "We also offer paid one-to-one and team AI tutoring, in person or remotely.",
  /** For structured-data offer catalogues. */
  catalog: [
    "One-to-one AI tutoring (in person or online)",
    "Team AI tutoring (in person or online)",
  ],
} as const;

/**
 * The catalogue mirrors Coaley Peak's nine programmes (Foundation to Senior).
 * The first, AI Fundamentals for Business, is eight free online lessons: the
 * way in. The other eight run in person only, for a team, at the standard day
 * rate with a minimum group size. `price` stays numeric for structured data
 * (0 = free, the day rate otherwise); pages decide how to display it.
 */
export const COURSES = [
  {
    slug: "ai-fundamentals-for-business",
    title: "AI Fundamentals for Business",
    level: "beginner",
    free: true,
    format: "online",
    delivery: "Online, self-paced",
    duration: "1 hour · 8 lessons",
    price: 0,
    description:
      "A focused one-hour course, eight short lessons, that takes a complete beginner to confident with everyday AI for UK businesses and charities. Free, online, and yours to start this afternoon. The sound first step before the in-person work.",
  },
  {
    slug: "ai-for-marketing-teams",
    title: "AI for Marketing Teams",
    level: "intermediate",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "Put AI to work on the campaigns, the copy and the customer research your marketing people do every week. A full day in the room with your team.",
  },
  {
    slug: "ai-for-finance-and-operations",
    title: "AI for Finance & Operations",
    level: "intermediate",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "Where AI saves real hours in finance and operations: reporting, reconciliation, forecasting and the daily admin. Hands-on, with your own numbers.",
  },
  {
    slug: "ai-for-sales-and-business-development",
    title: "AI for Sales & Business Development",
    level: "intermediate",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "Faster proposals, sharper research, better follow-up. A practical day teaching your sales team to sell with AI, not hide behind it.",
  },
  {
    slug: "ai-leadership-and-governance",
    title: "AI Leadership & Governance",
    level: "advanced",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "For the people who set the rules. How to lead an AI rollout, set policy, and keep the business on the right side of the line.",
  },
  {
    slug: "responsible-ai-in-practice",
    title: "Responsible AI in Practice",
    level: "intermediate",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "The practical side of using AI safely: data protection, bias, accuracy, and the records you need to keep. Plain rules your team can follow.",
  },
  {
    slug: "ai-for-cost-reduction",
    title: "AI for Cost Reduction",
    level: "advanced",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "Find the waste and take it out. A working day spent using AI to cut real cost across the business, with your own figures on the table.",
  },
  {
    slug: "ai-for-operational-performance",
    title: "AI for Operational Performance",
    level: "advanced",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "Get more out of the same team and the same week. AI put to work on planning, throughput, and the bottlenecks that slow you down.",
  },
  {
    slug: "ai-for-business-transformation",
    title: "AI for Business Transformation",
    level: "advanced",
    free: false,
    format: "in-person",
    delivery: "In person, on-site",
    duration: "One day",
    price: TRAINING_DAY_RATE,
    description:
      "The senior session. How to take AI from a handful of clever tricks to a change that runs right through the business.",
  },
] as const;

/**
 * Loyalty discounts off the in-person day rate, by how many in-person courses a
 * team has booked. Surfaced prominently in the rewards/gamification system to
 * encourage progression from the free online course into in-person training,
 * and quoted in the completion emails to the learner and their team manager.
 * The code is what they mention when booking (via the team enquiry form).
 */
export const COURSE_DISCOUNTS = [
  { order: 1, ordinal: "first", amount: 500, code: "FIRST500" },
  { order: 2, ordinal: "second", amount: 300, code: "NEXT300" },
  { order: 3, ordinal: "third", amount: 200, code: "THIRD200" },
] as const;

export type CourseDiscount = (typeof COURSE_DISCOUNTS)[number];

/** Paid, ongoing mentoring: the steady hand after the workshop. */
export const MENTORING = {
  title: "AI Mentoring Programme",
  description:
    "When a single day in the room is not enough, our mentoring keeps a steady hand on the work. One-to-one or small-group sessions over the weeks that follow, so the new habits stick. Optional after any course, and charged separately.",
  cadence: "Ongoing, one-to-one or small group",
  rate: "£75 + VAT per hour",
} as const;

export const FREE_CONTENT_ORIGINAL_PRICE = 329;
